import React, { useEffect, useMemo, useState } from "react";
import useI18n from "../../i18n/useI18n";
import NavigationButton from "../ui/buttons/NavigationButton";

const THEME_KEY = "hookora_theme";

const PLAN_ORDER = {
  free: 0,
  creator: 1,
  pro: 2,
};

const TOOL_MIN_PLAN = {
  hooks: "free",
  titles: "free",
  captions: "free",
  hashtags: "free",
  descriptions: "free",
  scripts: "free",
  hook_angles: "creator",
  cta: "creator",
  thumbnail: "creator",
  humanizer: "creator",
  repurpose: "creator",
  ad_copy: "creator",
  carousels: "creator",
  viral_rewrite: "creator",
  positioning: "creator",
  hook_score: "creator",
  nigerian_rewrite: "creator",
  retention_intro: "creator",
  comment_to_content: "creator",
  trend_to_content: "pro",
  brand_voice_trainer: "pro",
  content_differentiation: "pro",
};

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const normalizePlan = (plan) => {
  const cleaned = String(plan || "free").toLowerCase();
  return Object.prototype.hasOwnProperty.call(PLAN_ORDER, cleaned) ? cleaned : "free";
};

const getRequiredPlan = (toolKey) => TOOL_MIN_PLAN[toolKey] || "creator";

const canAccessTool = (currentPlan, toolKey) =>
  PLAN_ORDER[normalizePlan(currentPlan)] >= PLAN_ORDER[getRequiredPlan(toolKey)];

const StudioSidebar = ({
  activeTool,
  setActiveTool,
  isMobile = false,
  currentPlan = "free",
  onLockedToolAttempt,
}) => {
  const [theme, setTheme] = useState(getStoredTheme());
  const { t } = useI18n();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";
  const normalizedPlan = normalizePlan(currentPlan);

  const tools = useMemo(
    () => [
      {
        key: "hooks",
        label: t("tools.hooks", "Hooks"),
        icon: "⚡",
        description: t(
          "studioSidebar.toolDescriptions.hooks",
          "Generate direct opening lines you can use fast."
        ),
      },
      {
        key: "hook_angles",
        label: t("tools.hookAngles", "Hook Angles"),
        icon: "🧭",
        description: t(
          "studioSidebar.toolDescriptions.hookAngles",
          "Explore multiple directions before choosing your best hook."
        ),
      },
      {
        key: "titles",
        label: t("tools.titles", "Titles"),
        icon: "🏷️",
        description: t(
          "studioSidebar.toolDescriptions.titles",
          "Create stronger titles for videos, posts, and content packaging."
        ),
      },
      {
        key: "captions",
        label: t("tools.captions", "Captions"),
        icon: "✍️",
        description: t(
          "studioSidebar.toolDescriptions.captions",
          "Write clearer captions that support your message and platform."
        ),
      },
      {
        key: "hashtags",
        label: t("tools.hashtags", "Hashtags"),
        icon: "#",
        description: t(
          "studioSidebar.toolDescriptions.hashtags",
          "Generate relevant hashtags to support discovery and reach."
        ),
      },
      {
        key: "descriptions",
        label: t("tools.descriptions", "Descriptions"),
        icon: "📝",
        description: t(
          "studioSidebar.toolDescriptions.descriptions",
          "Rewrite or improve descriptions for stronger clarity and flow."
        ),
      },
      {
        key: "scripts",
        label: t("tools.scripts", "Scripts"),
        icon: "🎬",
        description: t(
          "studioSidebar.toolDescriptions.scripts",
          "Build short scripts with stronger structure and delivery."
        ),
      },
      {
        key: "cta",
        label: t("tools.ctas", "CTAs"),
        icon: "📣",
        description: t(
          "studioSidebar.toolDescriptions.ctas",
          "Create calls to action that feel sharper and more intentional."
        ),
      },
      {
        key: "thumbnail",
        label: t("tools.thumbnailAnalyzer", "Thumbnail Analyzer"),
        icon: "🖼️",
        description: t(
          "studioSidebar.toolDescriptions.thumbnail",
          "Improve thumbnail text for stronger first impressions."
        ),
      },
      {
        key: "humanizer",
        label: t("tools.aiHumanizer", "AI Humanizer"),
        icon: "🧠",
        description: t(
          "studioSidebar.toolDescriptions.humanizer",
          "Make your writing feel more natural, human, and readable."
        ),
      },
      {
        key: "repurpose",
        label: t("tools.repurposer", "Repurposer"),
        icon: "🔁",
        description: t(
          "studioSidebar.toolDescriptions.repurpose",
          "Turn one idea into multiple usable content outputs."
        ),
      },
      {
        key: "ad_copy",
        label: t("tools.adCopy", "Ad Copy"),
        icon: "🎯",
        description: t(
          "studioSidebar.toolDescriptions.adCopy",
          "Create sharper promotional copy for offers and campaigns."
        ),
      },
      {
        key: "carousels",
        label: t("tools.carousels", "Carousels"),
        icon: "🧩",
        description: t(
          "studioSidebar.toolDescriptions.carousels",
          "Turn ideas into structured carousel content faster."
        ),
      },
      {
        key: "viral_rewrite",
        label: t("tools.viralRewrite", "Viral Rewrite"),
        icon: "🔥",
        description: t(
          "studioSidebar.toolDescriptions.viralRewrite",
          "Rewrite weak copy into something sharper and more scroll-stopping."
        ),
      },
      {
        key: "positioning",
        label: t("tools.offerPositioning", "Offer Positioning"),
        icon: "💎",
        description: t(
          "studioSidebar.toolDescriptions.positioning",
          "Clarify how your offer should be framed to feel stronger."
        ),
      },
      {
        key: "hook_score",
        label: t("tools.hookScore", "Hook Score"),
        icon: "📊",
        description: t(
          "studioSidebar.toolDescriptions.hookScore",
          "Analyze how strong your hook feels before you publish."
        ),
      },
      {
        key: "trend_to_content",
        label: t("tools.trendConverter", "Trend Converter"),
        icon: "📈",
        description: t(
          "studioSidebar.toolDescriptions.trendToContent",
          "Turn trends into usable content angles and ideas."
        ),
      },
      {
        key: "nigerian_rewrite",
        label: t("tools.nigerianRewrite", "Nigerian Rewrite"),
        icon: "🇳🇬",
        description: t(
          "studioSidebar.toolDescriptions.nigerianRewrite",
          "Rewrite content to feel more natural for a Nigerian audience."
        ),
      },
      {
        key: "retention_intro",
        label: t("tools.retentionIntro", "Retention Intro"),
        icon: "🎥",
        description: t(
          "studioSidebar.toolDescriptions.retentionIntro",
          "Build stronger intros designed to hold attention earlier."
        ),
      },
      {
        key: "brand_voice_trainer",
        label: t("tools.brandVoiceTrainer", "Brand Voice Trainer"),
        icon: "🎙️",
        description: t(
          "studioSidebar.toolDescriptions.brandVoiceTrainer",
          "Train outputs to feel more aligned with your voice and tone."
        ),
      },
      {
        key: "content_differentiation",
        label: t("tools.contentDifferentiation", "Content Differentiation"),
        icon: "✨",
        description: t(
          "studioSidebar.toolDescriptions.contentDifferentiation",
          "Make your content feel more distinct and less generic."
        ),
      },
      {
        key: "comment_to_content",
        label: t("tools.commentIdeas", "Comment Ideas"),
        icon: "💬",
        description: t(
          "studioSidebar.toolDescriptions.commentToContent",
          "Turn audience comments into stronger content directions."
        ),
      },
    ],
    [t]
  );

  const colors = useMemo(
    () => ({
      railBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      railBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      railShadow: isDark
        ? "0 12px 28px rgba(2, 6, 23, 0.28)"
        : "0 8px 22px rgba(15, 23, 42, 0.06)",
      heading: isDark ? "#f8fafc" : "#111827",
      muted: isDark ? "#94a3b8" : "#6b7280",
      chipBg: isDark ? "rgba(15,23,42,0.96)" : "#ffffff",
      chipBorder: isDark ? "rgba(148,163,184,0.16)" : "#dbe2ea",
      chipText: isDark ? "#e2e8f0" : "#111827",
      chipMuted: isDark ? "#94a3b8" : "#64748b",
      chipActiveBg: isDark
        ? "linear-gradient(135deg, rgba(79,70,229,0.24), rgba(37,99,235,0.14))"
        : "linear-gradient(135deg, #eef2ff, #f8fbff)",
      chipActiveBorder: isDark ? "#818cf8" : "#4f46e5",
      chipActiveText: isDark ? "#c7d2fe" : "#312e81",
      chipActiveMuted: isDark ? "#a5b4fc" : "#4338ca",
      iconBg: isDark ? "rgba(30,41,59,0.95)" : "#f8fafc",
      iconBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      iconText: isDark ? "#e2e8f0" : "#334155",
      activeIconBg: isDark ? "rgba(99,102,241,0.18)" : "#e0e7ff",
      activeIconBorder: isDark ? "rgba(129,140,248,0.38)" : "#c7d2fe",
      activeIconText: isDark ? "#c7d2fe" : "#3730a3",

      freeBadgeBg: isDark ? "rgba(30,41,59,0.95)" : "#f8fafc",
      freeBadgeBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      freeBadgeText: isDark ? "#cbd5e1" : "#475569",

      creatorBadgeBg: isDark ? "rgba(37,99,235,0.16)" : "#dbeafe",
      creatorBadgeBorder: isDark ? "rgba(96,165,250,0.28)" : "#93c5fd",
      creatorBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",

      proBadgeBg: isDark ? "rgba(109,40,217,0.18)" : "#f3e8ff",
      proBadgeBorder: isDark ? "rgba(216,180,254,0.32)" : "#d8b4fe",
      proBadgeText: isDark ? "#e9d5ff" : "#7c3aed",

      lockedBg: isDark ? "rgba(120,53,15,0.16)" : "#fff7ed",
      lockedBorder: isDark ? "rgba(251,191,36,0.24)" : "#fdba74",
      lockedText: isDark ? "#fde68a" : "#9a3412",
    }),
    [isDark]
  );

  const getTierLabel = (toolKey) => {
    const required = getRequiredPlan(toolKey);
    if (required === "pro") return t("plans.pro", "Pro");
    if (required === "creator") return t("plans.creator", "Creator");
    return t("plans.free", "Free");
  };

  const getBadgeColors = (requiredPlan, isLocked) => {
    if (isLocked) {
      return {
        background: colors.lockedBg,
        border: colors.lockedBorder,
        color: colors.lockedText,
      };
    }

    if (requiredPlan === "pro") {
      return {
        background: colors.proBadgeBg,
        border: colors.proBadgeBorder,
        color: colors.proBadgeText,
      };
    }

    if (requiredPlan === "creator") {
      return {
        background: colors.creatorBadgeBg,
        border: colors.creatorBadgeBorder,
        color: colors.creatorBadgeText,
      };
    }

    return {
      background: colors.freeBadgeBg,
      border: colors.freeBadgeBorder,
      color: colors.freeBadgeText,
    };
  };

  return (
    <div
      style={{
        border: `1px solid ${colors.railBorder}`,
        borderRadius: isMobile ? "18px" : "20px",
        padding: isMobile ? "12px" : "14px",
        background: colors.railBg,
        boxShadow: colors.railShadow,
      }}
    >
      <div style={{ marginBottom: "10px" }}>
        <h3
          style={{
            margin: 0,
            color: colors.heading,
            fontSize: "16px",
            fontWeight: 800,
          }}
        >
          {t("studioSidebar.title", "Studio Tools")}
        </h3>

        <p
          style={{
            margin: "5px 0 0",
            color: colors.muted,
            fontSize: isMobile ? "12px" : "13px",
            lineHeight: "1.45",
          }}
        >
          {t(
            "studioSidebar.subtitle",
            "Choose a tool based on the kind of content you want to create."
          )}
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: isMobile ? "10px" : "12px",
          overflowX: "auto",
          paddingBottom: "4px",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
        }}
      >
        {tools.map((tool) => {
          const isActive = activeTool === tool.key;
          const requiredPlan = getRequiredPlan(tool.key);
          const isLocked = !canAccessTool(normalizedPlan, tool.key);
          const badgeColors = getBadgeColors(requiredPlan, isLocked);

          return (
            <NavigationButton
              key={tool.key}
              type="button"
              onClick={() => {
                if (isLocked) {
                  onLockedToolAttempt?.({
                    ...tool,
                    requiredPlan,
                    tierLabel: getTierLabel(tool.key),
                  });
                  return;
                }
                setActiveTool(tool.key);
              }}
              style={{
                minWidth: isMobile ? "148px" : "210px",
                maxWidth: isMobile ? "148px" : "210px",
                padding: isMobile ? "10px" : "12px",
                borderRadius: "18px",
                border: `1px solid ${
                  isLocked
                    ? colors.lockedBorder
                    : isActive
                    ? colors.chipActiveBorder
                    : colors.chipBorder
                }`,
                background: isLocked
                  ? colors.lockedBg
                  : isActive
                  ? colors.chipActiveBg
                  : colors.chipBg,
                color: isLocked
                  ? colors.lockedText
                  : isActive
                  ? colors.chipActiveText
                  : colors.chipText,
                cursor: "pointer",
                textAlign: "left",
                flexShrink: 0,
                boxShadow: isActive
                  ? isDark
                    ? "0 10px 24px rgba(79,70,229,0.14)"
                    : "0 10px 24px rgba(79,70,229,0.10)"
                  : "none",
                position: "relative",
                transition: "all 0.22s ease",
                transform: isActive ? "translateY(-2px)" : "translateY(0px)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "8px",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    width: isMobile ? "36px" : "40px",
                    height: isMobile ? "36px" : "40px",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize:
                      tool.key === "hashtags" ||
                      tool.key === "humanizer" ||
                      tool.key === "hook_angles" ||
                      tool.key === "cta" ||
                      tool.key === "repurpose" ||
                      tool.key === "ad_copy" ||
                      tool.key === "carousels" ||
                      tool.key === "viral_rewrite" ||
                      tool.key === "positioning" ||
                      tool.key === "hook_score" ||
                      tool.key === "trend_to_content" ||
                      tool.key === "nigerian_rewrite" ||
                      tool.key === "retention_intro" ||
                      tool.key === "brand_voice_trainer" ||
                      tool.key === "content_differentiation" ||
                      tool.key === "comment_to_content"
                        ? "18px"
                        : "16px",
                    fontWeight: tool.key === "hashtags" ? 800 : 700,
                    background: isLocked
                      ? colors.lockedBg
                      : isActive
                      ? colors.activeIconBg
                      : colors.iconBg,
                    border: `1px solid ${
                      isLocked
                        ? colors.lockedBorder
                        : isActive
                        ? colors.activeIconBorder
                        : colors.iconBorder
                    }`,
                    color: isLocked
                      ? colors.lockedText
                      : isActive
                      ? colors.activeIconText
                      : colors.iconText,
                    flexShrink: 0,
                  }}
                >
                  {tool.icon}
                </div>

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "5px 8px",
                    borderRadius: "999px",
                    border: `1px solid ${badgeColors.border}`,
                    background: badgeColors.background,
                    color: badgeColors.color,
                    fontSize: "10px",
                    fontWeight: 800,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {getTierLabel(tool.key)}
                  {isLocked ? <span style={{ fontSize: "11px" }}>🔒</span> : null}
                </div>
              </div>

              <div
                style={{
                  fontWeight: 800,
                  fontSize: isMobile ? "14px" : "15px",
                  lineHeight: "1.2",
                  marginBottom: "4px",
                }}
              >
                {tool.label}
              </div>

              <div
                style={{
                  fontSize: isMobile ? "11px" : "12px",
                  lineHeight: "1.45",
                  color: isLocked
                    ? colors.lockedText
                    : isActive
                    ? colors.chipActiveMuted
                    : colors.chipMuted,
                }}
              >
                {isLocked
                  ? `${getTierLabel(tool.key)} ${t("studioSidebar.lockedSuffix", "tool")}`
                  : tool.description}
              </div>
            </NavigationButton>
          );
        })}
      </div>
    </div>
  );
};

export default StudioSidebar;